import React, { useState } from 'react';
import { Form, Input, Button, Alert, Typography, Result } from 'antd';
import { UserOutlined, MailOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import axios from 'axios';

const { Title, Text, Paragraph } = Typography;

const ForgotPassword = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [email, setEmail] = useState('');

  const handleSubmit = async (values) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/auth/forgot-password`,
        { email: values.email }
      );

      if (response.data.success) {
        setSuccess(true);
        setEmail(values.email);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 w-full">
        <div className="flex min-h-screen w-full">
          {/* Left Column - Logo and Branding */}
          <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-blue-600 to-blue-800 relative overflow-hidden">
            {/* Background Pattern */}
            <div className="absolute inset-0 bg-black bg-opacity-5">
              <div className="absolute inset-0" style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.03'%3E%3Ccircle cx='20' cy='20' r='1.5'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
              }} />
            </div>
            
            {/* Content */}
            <div className="relative z-10 flex flex-col justify-center items-center w-full px-8 xl:px-12">
              {/* Logo */}
              <div className="mb-12">
                <img 
                  src="/qqcrm-logo.png" 
                  alt="QQCRM Logo" 
                  className="h-16 xl:h-20 w-auto filter brightness-0 invert"
                />
              </div>
              
              {/* Success Icon */}
              <div className="text-center text-white max-w-md">
                <div className="mb-6">
                  <MailOutlined style={{ fontSize: 64, color: '#fff' }} />
                </div>
                <h1 className="text-3xl xl:text-4xl font-bold mb-6 leading-tight">
                  Check Your Email
                </h1>
                <p className="text-lg xl:text-xl text-blue-100 leading-relaxed">
                  We've sent password reset instructions to your email address.
                </p>
              </div>
            </div>
          </div>

          {/* Right Column - Success Message */}
          <div className="w-full lg:w-1/2 flex flex-col">
            {/* Header */}
            <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 sm:px-6 lg:px-8 py-6">
              <div className="text-center lg:text-left">
                {/* Mobile Logo */}
                <div className="lg:hidden mb-6">
                  <img 
                    src="/qqcrm-logo.png" 
                    alt="QQCRM Logo" 
                    className="h-12 w-auto mx-auto"
                  />
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 bg-white dark:bg-gray-800 px-4 sm:px-6 lg:px-8 py-8 overflow-y-auto w-full flex items-center justify-center">
              <div className="w-full max-w-md">
                <Result
                  status="success"
                  title="Email Sent Successfully!"
                  subTitle={
                    <div className="space-y-4">
                      <Paragraph className="text-gray-600 dark:text-gray-400">
                        We've sent password reset instructions to:
                      </Paragraph>
                      <Paragraph strong className="text-lg text-gray-900 dark:text-gray-100">
                        {email}
                      </Paragraph>
                      <Paragraph className="text-gray-600 dark:text-gray-400">
                        Please check your email and follow the instructions to reset your password.
                        The link will expire in 1 hour.
                      </Paragraph>
                      <Paragraph className="text-sm text-gray-500 dark:text-gray-500">
                        Didn't receive the email? Check your spam folder or{' '}
                        <Button 
                          type="link" 
                          onClick={() => setSuccess(false)}
                          className="p-0 h-auto"
                        >
                          try again
                        </Button>
                      </Paragraph>
                    </div>
                  }
                  extra={[
                    <Link to="/auth/login" key="login">
                      <Button type="primary" size="large" icon={<ArrowLeftOutlined />}>
                        Back to Login
                      </Button>
                    </Link>
                  ]}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 w-full">
      <div className="flex min-h-screen w-full">
        {/* Left Column - Logo and Branding */}
        <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-blue-600 to-blue-800 relative overflow-hidden">
          {/* Background Pattern */}
          <div className="absolute inset-0 bg-black bg-opacity-5">
            <div className="absolute inset-0" style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.03'%3E%3Ccircle cx='20' cy='20' r='1.5'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }} />
          </div>
          
          {/* Content */}
          <div className="relative z-10 flex flex-col justify-center items-center w-full px-8 xl:px-12">
            {/* Logo */}
            <div className="mb-12">
              <img 
                src="/qqcrm-logo.png" 
                alt="QQCRM Logo" 
                className="h-16 xl:h-20 w-auto filter brightness-0 invert"
              />
            </div>
            
            {/* Branding Content */}
            <div className="text-center text-white max-w-md">
              <h1 className="text-3xl xl:text-4xl font-bold mb-6 leading-tight">
                Reset Your Password
              </h1>
              <p className="text-lg xl:text-xl text-blue-100 mb-10 leading-relaxed">
                Enter your email address and we'll send you instructions to reset your password.
              </p>
              
              {/* Features */}
              <div className="space-y-5 text-left">
                <div className="flex items-center space-x-4">
                  <div className="w-3 h-3 bg-blue-300 rounded-full flex-shrink-0"></div>
                  <span className="text-blue-100 text-base">Secure Password Reset</span>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="w-3 h-3 bg-blue-300 rounded-full flex-shrink-0"></div>
                  <span className="text-blue-100 text-base">Email Verification</span>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="w-3 h-3 bg-blue-300 rounded-full flex-shrink-0"></div>
                  <span className="text-blue-100 text-base">Quick & Easy Process</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Form */}
        <div className="w-full lg:w-1/2 flex flex-col">
          {/* Header */}
          <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 sm:px-6 lg:px-8 py-6">
            <div className="text-center lg:text-left">
              {/* Mobile Logo */}
              <div className="lg:hidden mb-6">
                <img 
                  src="/qqcrm-logo.png" 
                  alt="QQCRM Logo" 
                  className="h-12 w-auto mx-auto"
                />
              </div>
              
              <Title level={2} className="text-gray-900 dark:text-gray-100 mb-2">
                Forgot Password?
              </Title>
              <Text className="text-gray-600 dark:text-gray-400 text-lg">
                No worries, we'll send you reset instructions
              </Text>
            </div>
          </div>

          {/* Form Content */}
          <div className="flex-1 bg-white dark:bg-gray-800 px-4 sm:px-6 lg:px-8 py-8 overflow-y-auto w-full flex items-center justify-center">
            <div className="w-full max-w-md">
              {error && (
                <Alert
                  message="Error"
                  description={error}
                  type="error"
                  showIcon
                  closable
                  onClose={() => setError(null)}
                  className="mb-6"
                />
              )}

              <Form
                form={form}
                name="forgot-password"
                onFinish={handleSubmit}
                layout="vertical"
                size="large"
                autoComplete="on"
              >
                <Form.Item
                  name="email"
                  label="Email Address"
                  rules={[
                    { required: true, message: 'Please enter your email' },
                    { type: 'email', message: 'Please enter a valid email' }
                  ]}
                >
                  <Input
                    prefix={<UserOutlined className="text-gray-400" />}
                    placeholder="Enter your email address"
                    autoComplete="email"
                    autoFocus
                  />
                </Form.Item>

                <Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={loading}
                    className="w-full h-12 text-base font-medium"
                  >
                    {loading ? 'Sending...' : 'Send Reset Link'}
                  </Button>
                </Form.Item>

                <div className="text-center">
                  <Link 
                    to="/auth/login" 
                    className="text-primary-600 hover:text-primary-500 font-medium inline-flex items-center gap-2"
                  >
                    <ArrowLeftOutlined />
                    Back to Login
                  </Link>
                </div>
              </Form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
